import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, XCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import type { SubscriptionWarningData } from "../../services/admin/types/billing.types";

interface SubscriptionWarningBannerProps {
  subscriptionWarning?: SubscriptionWarningData | null;
  limitToBilling: boolean;
}

const SubscriptionWarningBanner: React.FC<SubscriptionWarningBannerProps> = ({
  subscriptionWarning,
  limitToBilling,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isPastDue = subscriptionWarning?.status === "PastDue";
  const isSuspendedOrInactive = limitToBilling && !isPastDue;

  // Don't show if subscription is healthy and not in limitToBilling mode
  if (!isPastDue && !isSuspendedOrInactive) return null;

  const formatTimeRemaining = (dateStr?: string): string | null => {
    if (!dateStr) return null;
    const end = new Date(dateStr);
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    if (diffMs <= 0) return null;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return t("billing.warning.days_remaining", "{{count}} day(s) remaining", {
        count: days,
      });
    }
    if (hours > 0) {
      return t("billing.warning.hours_remaining", "{{hours}}h {{minutes}}m remaining", {
        hours,
        minutes,
      });
    }
    return t("billing.warning.minutes_remaining", "{{count}} minute(s) remaining", {
      count: minutes,
    });
  };

  if (isPastDue) {
    const timeRemaining = formatTimeRemaining(subscriptionWarning?.gracePeriodEndsAt);

    return (
      <div className="px-3 sm:px-5 lg:px-8 pt-3">
        <Alert
          variant="default"
          className="border-amber-500/50 bg-amber-500/10 dark:bg-amber-500/5"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <AlertTitle className="text-amber-800 dark:text-amber-200 font-semibold">
            {t(
              "billing.warning.payment_failed_title",
              "Payment Failed — Action Required"
            )}
          </AlertTitle>
          <AlertDescription className="mt-1">
            <div className="text-amber-700 dark:text-amber-300 text-sm">
              {t(
                "billing.warning.payment_failed_body",
                "Your last payment failed. Update your payment method to avoid losing access. Guest services will be suspended if not resolved."
              )}
              {timeRemaining && (
                <span className="ml-1 font-semibold">{timeRemaining}</span>
              )}
            </div>
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                className="border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                onClick={() => navigate("/admin/billing")}
              >
                {t("billing.warning.go_to_billing", "Go to Billing")}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Suspended / expired / no subscription — red banner
  return (
    <div className="px-3 sm:px-5 lg:px-8 pt-3">
      <Alert
        variant="destructive"
        className="border-red-500/50 bg-red-500/10 dark:bg-red-500/5"
      >
        <XCircle className="h-5 w-5" />
        <AlertTitle className="font-semibold">
          {t(
            "billing.warning.inactive_title",
            "Subscription Inactive"
          )}
        </AlertTitle>
        <AlertDescription className="mt-1">
          <div className="text-sm">
            {t(
              "billing.warning.inactive_body",
              "Your subscription is inactive. Guest services are currently offline. Reactivate your subscription to restore access."
            )}
          </div>
          <div className="mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/billing")}
            >
              {t(
                "billing.warning.reactivate",
                "Reactivate Subscription"
              )}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default SubscriptionWarningBanner;
